import React, { useContext, useState, useEffect, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { Container, ButtonDiv, Header, InfosContainer, Section, ButtonContainer, TitleRow, ButtonRow, InfoGrid, InfoItem, InfoLabel, InfoValue, SimpleTable, SimpleTableRow, TableHeader, TableCell, SectionDivider, SectionSubtitle, ModalOverlay, ModalContent, ModalTitle, ModalText, ModalActions, ModalCancelButton, ModalConfirmButton } from './style'
import pencil from '../../../assets/production-flow/pencil.svg'
import copy from '../../../assets/production-flow/copy.svg'
import invoice from '../../../assets/production-flow/invoice.svg'
import link from '../../../assets/production-flow/link.svg'
import cross from '../../../assets/production-flow/cross.svg'
import ModalContext from '../../../contexts/ModalContext'
import { ServicesContext } from '../../../contexts/ServicesContext'
import { FeedbackContext } from '../../../contexts/FeedbackContext'
import axios from '../../../config/axios'
import SplitOPModal from './SplitOPModal'
import GeneratePDFModal from '../../Modals/ViewPCP/GeneratePDFModal'
import Status from '../../Modals/ViewPCP/Status'
import GenerateInspectionFormModal from '../../Modals/ViewPCP/GenerateInspectionForm'

enum SubModalSection {
  NONE = 0,
  SPLIT_OP = 1,
  GENERATE_PDF = 2,
  GENERATE_INSPECTION_FORM_PDF = 3
}

const getClientName = (client: any): string => {
  if (!client) return 'Cliente não informado'
  if (typeof client === 'object') return client.companyName || client.name || 'Cliente não informado'
  if (typeof client === 'string') return client
  return 'Cliente não informado'
}

const getWeight = (orderItem: any): string => {
  return orderItem?.weight?.toString() || '-'
}

const getItemDescription = (orderItem: any): string => {
  return orderItem?.ploomesProductName || 'Item não identificado'
}

const getMaterialName = (stockItem: any): string => {

  const stock = stockItem.Stock || stockItem.stock
  return stock?.description || stock?.name || stockItem.description || stockItem.name || 
         (stockItem.stockId ? `Material ID: ${stockItem.stockId}` : 'Material não identificado')
}

const getResourceName = (stockItem: any): string => {

  const resource = stockItem.Resource || stockItem.resource
  return resource?.name || resource?.description || 
         (stockItem.resourceId ? `Insumo ID: ${stockItem.resourceId}` : 'Insumo não identificado')
}

const api = {
  getHeaders: () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }),
  
  async fetchServiceData(serviceId: string) {
    const headers = this.getHeaders()
    const [basicData, servicesData] = await Promise.all([
      axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/${serviceId}`, { headers }),
      axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/services-of-op/${serviceId}`, { headers })
    ])
    
    return { basicData: basicData.data, servicesData: servicesData.data }
  },
  
  async fetchOrderItem(orderItemId: string) {

    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders/order`,
        { headers: this.getHeaders() }
      )
      const orders = Array.isArray(data) ? data : data?.data || []
      for (const order of orders) {
        const items = order.items || order.OrderItem || order.orderItems || []
        const found = items.find((i: any) => String(i.id) === String(orderItemId))
        if (found) return { ...found, order }
      }
      return null
    } catch (error) {
      console.error('Erro ao buscar order-item:', error)
      return null
    }
  },
  
  async integrateOmie(serviceId: string, materials: any[], resources: any[]) {
    const payload: any = {}
    
    if (materials.length > 0) {
      payload.materials = materials.map(item => ({
        unitOfMeasure: item.usedUnitOfMeasurement || 'KG',
        quantityToUse: (item.usedQuantity || '0').toString().replace(',', '.'),
        material: {
          id: item.Stock?.id || item.stock?.id || item.stockId || 0,
          name: getMaterialName(item),
        }
      }))
    }
    
    if (resources.length > 0) {
      payload.resources = resources.map(item => ({
        unitOfMeasure: item.usedUnitOfMeasurement || 'KG',
        quantityToUse: (item.usedQuantity || '0').toString().replace(',', '.'),
        material: {
          id: item.Resource?.id || item.resource?.id || item.resourceId || 0,
          name: getResourceName(item),
        }
      }))
    }

    await axios.post(
      `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/discount/${serviceId}`,
      payload,
      { 
        params: { onlyInOmie: true },
        headers: this.getHeaders()
      }
    )
  },
  
  async revertOP(serviceId: string) {
    const { data } = await axios.post(
      `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/revert-movement`,
      { id: Number(serviceId) },
      {
        headers: {
          'Content-Type': 'application/json',
          ...this.getHeaders()
        },
      }
    )
    return data
  }
}

const ViewOPServiceModal: React.FC = () => {
  const { setModalActive } = useContext(ModalContext)
  const { service, setService } = useContext(ServicesContext)
  const { setFeedback } = useContext(FeedbackContext)
  const navigate = useNavigate()
  
  const [subModalActive, setSubModalActive] = useState<SubModalSection>(SubModalSection.NONE)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [fullServiceData, setFullServiceData] = useState<any>(null)
  const [isReverting, setIsReverting] = useState(false)
  const [showRevertConfirm, setShowRevertConfirm] = useState(false)

  useEffect(() => {
    const loadServiceData = async () => {
      if (!service?.id || fullServiceData) return
      
      try {
        const { basicData, servicesData } = await api.fetchServiceData(service.id)
        
        let orderItem = basicData.orderItem
        if (!orderItem && basicData.orderItemId) {
          orderItem = await api.fetchOrderItem(basicData.orderItemId)
        }
        
        const enrichedData = {
          ...basicData,
          orderItem,
          service_stage: servicesData || [],
          StockInProduction: basicData.StockInProduction || basicData.stockInProduction || []
        }
        
        setFullServiceData(enrichedData)
        setService?.(enrichedData)
      } catch (error) {
        console.error('Erro ao carregar dados do serviço:', error)
        setFullServiceData(service)
      } finally {
        setIsLoadingData(false)
      }
    }

    loadServiceData()
  }, [service?.id, fullServiceData, setService])

  const data = fullServiceData || service
  const orderItem = data?.orderItem || data?.OrderItem
  const order = orderItem?.order || orderItem?.Order
  
  const orderInfo = {
    orderNumber: order?.saleNumberPloomes || '-',
    clientName: getClientName(order?.client || order?.Client || data?.client),
    weight: getWeight(orderItem),
    itemDescription: getItemDescription(orderItem),
    integratedWithOmie: order?.integratedWithOmie || false
  }

  const itemIndex = orderItem?.indexInOrder !== undefined ? orderItem.indexInOrder + 1 : 1
  const totalItems = order?.orderItems?.length || 1
  const opCode = data?.codeOfOP || `OP ${orderInfo.orderNumber} - ${itemIndex}/${totalItems}`
  const canSplit = data?.codeOfOP && !data.codeOfOP.includes('.')
  
  const stages = data?.service_stage || []
  const allStockInProduction = data?.StockInProduction || data?.stockInProduction || []
  const materials = allStockInProduction.filter((item: any) => item.stockId !== null)
  const resources = allStockInProduction.filter((item: any) => item.resourceId !== null)
  
  const materialsLocked = materials.length > 0 || resources.length > 0

  const handleCloseSubModal = () => {
    navigate(window.location.pathname, { replace: true })
    setSubModalActive(SubModalSection.NONE)
  }

  const handleOpenPDFModal = () => {
    if (!service?.id) return
    navigate(`${window.location.pathname}?id=${service.id}`, { replace: true })
    setSubModalActive(SubModalSection.GENERATE_PDF)
  }
  const handleOpenInspectionFormModal = () => {
    if (!service?.id) return
    navigate(`${window.location.pathname}?id=${service.id}`, { replace: true })
    setSubModalActive(SubModalSection.GENERATE_INSPECTION_FORM_PDF)
  }
  const handleEditClick = () => {
    setModalActive(false)
    navigate(`/panel/production/edit/${service.id}`)
  }

  const handleReintegrateOmie = async () => {
    if (materials.length === 0 && resources.length === 0) return
    
    setIsLoading(true)
    try {
      await api.integrateOmie(service.id, materials, resources)
      setFeedback({
        isOpen: true,
        statusOk: true,
        message: 'Integração com Omie realizada com sucesso!'
      })
    } catch (error) {
      console.error('Erro ao integrar na Omie:', error)
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : 'Erro desconhecido'
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: `Erro ao integrar na Omie: ${errorMessage}`
      })
    } finally {
      setIsLoading(false)
    }
  }
  
  const handleRevert = async () => {
    if (!service?.id) return

    setIsReverting(true)
    try {
      const response = await api.revertOP(service.id)

      if (response) {
        setFeedback({
          isOpen: true,
          statusOk: true,
          message: 'OP revertida com sucesso!'
        })
        setShowRevertConfirm(false)
        
        const { basicData, servicesData } = await api.fetchServiceData(service.id)
        let orderItem = basicData.orderItem
        if (!orderItem && basicData.orderItemId) {
          orderItem = await api.fetchOrderItem(basicData.orderItemId)
        }
        
        const enrichedData = {
          ...basicData,
          orderItem,
          service_stage: servicesData || [],
          StockInProduction: basicData.StockInProduction || basicData.stockInProduction || []
        }
        
        setFullServiceData(enrichedData)
        setService?.(enrichedData)
      }
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : 'Falha ao reverter a OP'

      setFeedback({
        isOpen: true,
        statusOk: false,
        message: errorMessage
      })
    } finally {
      setIsReverting(false)
    }
  }
  
  if (isLoadingData) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          Carregando dados do serviço...
        </div>
      </Container>
    )
  }
  
  return (
    <>
      <Container>
        <Header>
          <TitleRow>
            <div>
              <h2>Ordem de produção: {opCode}</h2>
            </div>
            <Status status={service?.status || 0} />
          </TitleRow>
          <ButtonRow style={{ marginTop: '16px' }}>
            {canSplit && (
              <ButtonDiv 
                variant="outline"
                onClick={() => setSubModalActive(SubModalSection.SPLIT_OP)}
              >
                <img src={copy} alt="Dividir" />
                Dividir OP
              </ButtonDiv>
            )}

              <ButtonDiv 
              variant="outline" 
              onClick={handleOpenInspectionFormModal}
            >
              <img src={invoice} alt="PDF" />
              Gerar ficha
            </ButtonDiv>
            
            <ButtonDiv 
              variant="outline" 
              onClick={handleOpenPDFModal}
            >
              <img src={invoice} alt="PDF" />
              Gerar PDF
            </ButtonDiv>
            <button
              onClick={() => setModalActive(false)}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                border: 'none',
                background: '#f4f4f4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: 0
              }}
            >
              <img src={cross} alt="Fechar"/>
            </button>
            
          </ButtonRow>
        </Header>

        <InfosContainer>
          <Section>
            <SectionSubtitle style={{ fontSize: '16px', marginBottom: '24px' }}>
              Informações gerais
            </SectionSubtitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Item</InfoLabel>
                <InfoValue>{orderInfo.itemDescription}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Pedido</InfoLabel>
                <InfoValue>{orderInfo.orderNumber}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Peso</InfoLabel>
                <InfoValue>{orderInfo.weight}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Cliente</InfoLabel>
                <InfoValue>{orderInfo.clientName}</InfoValue>
              </InfoItem>
            </InfoGrid>
          </Section>

          <SectionDivider />

          <Section>
            <SectionSubtitle>Materiais</SectionSubtitle>
            {materials.length > 0 ? (
              <SimpleTable className="compact-table">
                <thead>
                  <tr>
                    <TableHeader>Material</TableHeader>
                    <TableHeader style={{ textAlign: 'center', width: '120px' }}>Un. medida</TableHeader>
                    <TableHeader style={{ textAlign: 'right', width: '100px' }}>Qtd.</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {materials.map((stockItem: any, index: number) => (
                    <SimpleTableRow key={index}>
                      <TableCell>{getMaterialName(stockItem)}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>
                        {stockItem.usedUnitOfMeasurement || '-'}
                      </TableCell>
                      <TableCell style={{ textAlign: 'right' }}>
                        {stockItem.usedQuantity || '-'}
                      </TableCell>
                    </SimpleTableRow>
                  ))}
                </tbody>
              </SimpleTable>
            ) : (
              <p style={{ textAlign: 'center', color: '#666', padding: '24px 0' }}>
                Nenhum material cadastrado nesta OP
              </p>
            )}
          </Section>

          <SectionDivider />

          <Section>
            <SectionSubtitle>Insumos</SectionSubtitle>
            {resources.length > 0 ? (
              <SimpleTable className="compact-table">
                <thead>
                  <tr>
                    <TableHeader>Insumo</TableHeader>
                    <TableHeader style={{ textAlign: 'center', width: '120px' }}>Un. medida</TableHeader>
                    <TableHeader style={{ textAlign: 'right', width: '100px' }}>Qtd.</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {resources.map((stockItem: any, index: number) => (
                    <SimpleTableRow key={index}>
                      <TableCell>{getResourceName(stockItem)}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>
                        {stockItem.usedUnitOfMeasurement || '-'}
                      </TableCell>
                      <TableCell style={{ textAlign: 'right' }}>
                        {stockItem.usedQuantity || '-'}
                      </TableCell>
                    </SimpleTableRow>
                  ))}
                </tbody>
              </SimpleTable>
            ) : (
              <p style={{ textAlign: 'center', color: '#666', padding: '24px 0' }}>
                Nenhum insumo cadastrado nesta OP
              </p>
            )}
          </Section>

          <SectionDivider />

          <Section>
            <SectionSubtitle>Serviços</SectionSubtitle>
            {stages.length > 0 ? (
              <SimpleTable>
                <thead>
                  <tr>
                    <TableHeader>Serviço</TableHeader>
                    <TableHeader>Descrição</TableHeader>
                    <TableHeader>Observação</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {stages.map((item: any, index: number) => (
                    <SimpleTableRow key={index}>
                      <TableCell>{item.Stage?.name || item.stage?.name || '-'}</TableCell>
                      <TableCell>{item.description || '-'}</TableCell>
                      <TableCell>{item.observation || '-'}</TableCell>
                    </SimpleTableRow>
                  ))}
                </tbody>
              </SimpleTable>
            ) : (
              <p style={{ textAlign: 'center', color: '#666', padding: '24px 0' }}>
                Nenhum serviço cadastrado nesta OP
              </p>
            )}
          </Section>
        </InfosContainer>

        <ButtonContainer>
          <ButtonDiv 
            variant="ghost" 
            onClick={() => setModalActive(false)}
            style={{ 
              border: 'none', 
              color: '#0090d3',
              fontWeight: 'bold'
            }}
          >
            Fechar
          </ButtonDiv>
          {materialsLocked && (
            <ButtonDiv 
              variant="outline"
              onClick={() => setShowRevertConfirm(true)}
              disabled={isReverting}
              style={{ fontWeight: 'bold' }}
            >
              {isReverting ? 'Revertendo...' : 'Reverter OP'}
            </ButtonDiv>
          )}
          <ButtonDiv 
            variant="outline"
            onClick={handleReintegrateOmie}
            disabled={(materials.length === 0 && resources.length === 0) || isLoading}
            style={{ fontWeight: 'bold' }}
          >
            <img src={link} alt="Reintegrar" />
            {orderInfo.integratedWithOmie ? 'Reintegrar na Omie' : 'Integrar na Omie'}
          </ButtonDiv>
          <ButtonDiv 
            variant="primary" 
            onClick={handleEditClick}
            style={{ fontWeight: 'bold' }}
          >
            <img src={pencil} alt="Editar" />
            Editar ordem de produção
          </ButtonDiv>
        </ButtonContainer>
      </Container>

      {subModalActive === SubModalSection.SPLIT_OP && service?.id && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 10000 }}>
          <SplitOPModal 
            setModalActive={(value: any) => {
              if (!value) handleCloseSubModal()
              else setSubModalActive(value)
            }}
            id={service.id}
            setRefresh={() => {}}
          />
        </div>
      )}
      
      {subModalActive === SubModalSection.GENERATE_PDF && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 10000 }}>
          <GeneratePDFModal onClickClose={handleCloseSubModal} />
        </div>
      )}
      {subModalActive === SubModalSection.GENERATE_INSPECTION_FORM_PDF && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 10000 }}>
          <GenerateInspectionFormModal onClickClose={handleCloseSubModal} />
        </div>
      )}
      
      {showRevertConfirm && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Deseja realmente reverter a OP?</ModalTitle>
            <ModalText>
              Essa ação irá desbloquear os materiais/insumos para edição e reverter o
              abatimento do estoque. Você poderá editar e salvar novamente.
            </ModalText>
            <ModalActions>
              <ModalCancelButton
                onClick={() => setShowRevertConfirm(false)}
                disabled={isReverting}
              >
                Cancelar
              </ModalCancelButton>
              <ModalConfirmButton onClick={handleRevert} disabled={isReverting}>
                {isReverting ? 'Revertendo...' : 'Confirmar'}
              </ModalConfirmButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  )
}

export default ViewOPServiceModal